import { useCallback, useContext, useRef } from "react";
import { DateInput } from "@mantine/dates";
import { EditableInfoRowContext } from "./EditableInfoRowContext";
import {
  formatDateOnlyForDisplay,
  parseDateOnly,
  type DateOnlyString,
} from "../../utils/dateOnly";

interface DateEditorProps {
  defaultValue: string | null;
  onSave: (date: DateOnlyString | null) => void;
  width?: number;
}

const DISPLAY_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

function parseDateEditorInput(value: string): DateOnlyString | null {
  const canonicalValue = parseDateOnly(value);
  if (canonicalValue) return canonicalValue;

  const displayMatch = DISPLAY_DATE_PATTERN.exec(value.trim());
  if (!displayMatch) return null;
  return (
    parseDateOnly(`${displayMatch[3]}-${displayMatch[1]}-${displayMatch[2]}`) ??
    null
  );
}

export function DateEditor({
  defaultValue,
  onSave,
  width = 130,
}: DateEditorProps) {
  const setOverride = useContext(EditableInfoRowContext);
  const pendingValueRef = useRef<DateOnlyString | null>(
    parseDateOnly(defaultValue) ?? null
  );
  const hasChangedSinceFocusRef = useRef(false);

  const handleChange = useCallback((value: string | null) => {
    hasChangedSinceFocusRef.current = true;
    pendingValueRef.current = parseDateOnly(value) ?? null;
  }, []);

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (!hasChangedSinceFocusRef.current) return;
      hasChangedSinceFocusRef.current = false;

      const rawValue = event.currentTarget.value.trim();
      if (!rawValue) {
        setOverride?.("");
        onSave(null);
        return;
      }

      const pendingValue = pendingValueRef.current;
      if (!pendingValue) return;
      setOverride?.(formatDateOnlyForDisplay(pendingValue));
      onSave(pendingValue);
    },
    [onSave, setOverride]
  );

  return (
    <DateInput
      size="xs"
      defaultValue={parseDateOnly(defaultValue) ?? null}
      valueFormat="MM/DD/YYYY"
      dateParser={parseDateEditorInput}
      allowDeselect
      onChange={handleChange}
      onBlur={handleBlur}
      style={{ width }}
    />
  );
}
